'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  TrashIcon,
  ClipboardListIcon,
  UserIcon,
  ActivityIcon,
  MonitorIcon,
  CalendarIcon,
  HashIcon,
  AlertCircleIcon,
  GlobeIcon,
  ShieldIcon,
  ClockIcon,
  WifiIcon,
  WifiOffIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { useAuditLog, useDeleteAuditLog, useCopyToClipboard } from '@/hooks'
import { useMeStore } from '@/store'
import { AuditResultEnum } from '@/enums'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'

type PropsType = {
  id: string
}

const AuditLogShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data: auditLog, isLoading, error } = useAuditLog(id)
  const { mutate: deleteAuditLogMutate, isPending: isDeleting } = useDeleteAuditLog()
  const locale = useLocale()
  const { meState } = useMeStore()
  const canShowUser = hasPermission(meState.response, 'USERS', 'SHOW')

  if (isLoading) {
    return <AuditLogShowSkeleton />
  }
  if (error || !auditLog) {
    return <ContentError error={error} listUrl="/logs/audit" />
  }

  const confirmDelete = () => {
    deleteAuditLogMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
        toast.success(t('deleted_successfully'))
        router.push('/logs/audit')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message ?? t('error_deleting'))
        setDeleteId(null)
      }
    })
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href="/logs/audit">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('back')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold">
                <ClipboardListIcon className="h-6 w-6" />
                {t('audit_log')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">{auditLog.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(auditLog.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            <Button variant="destructive" size="sm" onClick={() => setDeleteId(id)}>
              <TrashIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('delete')}</span>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('general_information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('email')}
                </p>
                <p className="font-medium">{auditLog.user?.email ?? auditLog.email}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <HashIcon className="h-3.5 w-3.5" />
                  {t('user_id')}
                </p>
                <p className="font-mono text-sm">{auditLog.userId}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <ActivityIcon className="h-3.5 w-3.5" />
                  {t('module')}
                </p>
                <Badge variant="outline">{t(`module_${auditLog.module}` as any)}</Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('action')}</p>
                <Badge variant="secondary">{t(`audit_action_${auditLog.action}` as any)}</Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('result')}</p>
                <Badge variant={auditLog.result === AuditResultEnum.SUCCESS ? 'success' : 'destructive'}>
                  {t(`audit_result_${auditLog.result}` as any)}
                </Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MonitorIcon className="h-3.5 w-3.5" />
                  {t('ip')}
                </p>
                <p className="font-mono text-sm">{auditLog.ip}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t('action_at')}
                </p>
                <p className="text-sm">{formatDateTime(auditLog.createdAt, locale)}</p>
              </div>

              {auditLog.contentId && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <HashIcon className="h-3.5 w-3.5" />
                    {t('content_id')}
                  </p>
                  <p className="font-mono text-sm">{auditLog.contentId}</p>
                </div>
              )}
            </div>

            {auditLog.contentIds && auditLog.contentIds.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('content_ids')}</p>
                  <div className="flex flex-wrap gap-1">
                    {auditLog.contentIds.map(cid => (
                      <Badge key={cid} variant="outline" className="font-mono text-xs">
                        {cid}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {auditLog.errorMessage && (
              <>
                <Separator className="my-4" />
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <AlertCircleIcon className="text-destructive h-3.5 w-3.5" />
                    {t('error_message')}
                  </p>
                  <p className="text-destructive text-sm">{auditLog.errorMessage}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {auditLog.user && canShowUser && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('user_information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <UserIcon className="h-3.5 w-3.5" />
                    {t('full_name')}
                  </p>
                  <p className="font-medium">{auditLog.user.fullName}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <WifiIcon className="h-3.5 w-3.5" />
                    {t('status')}
                  </p>
                  <Badge variant={auditLog.user.online ? 'success' : 'secondary'}>
                    {auditLog.user.online ? (
                      <>
                        <WifiIcon className="mr-1 h-3 w-3" />
                        {t('online')}
                      </>
                    ) : (
                      <>
                        <WifiOffIcon className="mr-1 h-3 w-3" />
                        {t('offline')}
                      </>
                    )}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {t('timezone')}
                  </p>
                  <p className="text-sm">{auditLog.user.timezone}</p>
                </div>

                {auditLog.user.roles.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1 text-sm">
                      <ShieldIcon className="h-3.5 w-3.5" />
                      {t('roles')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {auditLog.user.roles.map(role => (
                        <Badge key={role} variant="outline" className="font-mono text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('request_details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('method')}</p>
                <Badge>{auditLog.method}</Badge>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <GlobeIcon className="h-3.5 w-3.5" />
                  {t('path')}
                </p>
                <p className="font-mono text-sm">{auditLog.path}</p>
              </div>

              {auditLog.userAgent && (
                <div className="col-span-full space-y-1">
                  <p className="text-muted-foreground text-sm">{t('user_agent')}</p>
                  <p className="text-muted-foreground font-mono text-xs break-all">{auditLog.userAgent}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default Detail
